import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAppShell } from '../contexts/AppShellContext';
import { useAuth } from '../contexts/AuthContext';
import { getMaterialReceipts, updateMaterialReceipt, confirmReceiptMatch } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import { draftFromRow, buildReceiptPatch, draftToMatchJobs, type ReceiptDraft } from '../data/receiptDraft';
import { maybeCreateOvers } from '../services/oversWorkflow';
import { ReceiptDrawer } from '../components/chat/ReceiptDrawer';
import { PageHeader } from '../components/PageHeader';
import { pillStyle } from '../utils/pillStyle';
import { windowBounds, inWindow, windowCounts, type ScopeValue } from '../utils/timeWindow';

// Custom-range views can be huge — cap rendered rows and tell the user to narrow.
const RENDER_CAP = 200;

// job_status → pill style (reuses existing sp-* classes)
const STATUS_MAP: Record<string, { cls: string }> = {
  Received: { cls: 'sp-r' },
  Sample:   { cls: 'sp-v' },
  Mailing:  { cls: 'sp-m' },
  Complete: { cls: 'sp-r' },
  Aborted:  { cls: 'sp-e' },
  'CS-X':   { cls: 'sp-w' },
  PSD:      { cls: 'sp-r' },
  TSR:      { cls: 'sp-o' },
  X:        { cls: 'sp-r' },
};

const CATEGORY = {
  P:  { label: 'Proxy',       cls: 'tp tp-px' },
  CA: { label: 'Corp Action', cls: 'tp tp-ca' },
  R:  { label: 'Regulatory',  cls: 'tp tp-rg' },
  I:  { label: 'Issuer',      cls: 'tp tp-px' },
} as const;

const TABS = [
  { id: 'proxy',      label: 'Proxy, Corp Action & Issuer' },
  { id: 'regulatory', label: 'Regulatory'                  },
];

const FILTERS: Record<string, string[]> = {
  proxy:      ['All', 'Received', 'Sample', 'Mailing', 'Complete', 'Aborted'],
  regulatory: ['All', 'CS-X', 'PSD', 'TSR', 'X'],
};

// Job-category pills for the Proxy / Corp Action / Issuer tab.
const CATEGORY_FILTERS = [
  { id: 'All', label: 'All' },
  { id: 'P',   label: 'Proxy' },
  { id: 'CA',  label: 'Corp Action' },
  { id: 'I',   label: 'Issuer' },
];

// Active filter-pill colours, matched to the badge palette in index.css (.sp-* / .tp-*).
const PILL_PALETTE: Record<string, { bg: string; fg: string; border?: string }> = {
  'sp-r':  { bg: '#E9F6F6', fg: '#0F6E56' },
  'sp-v':  { bg: '#DBEAFE', fg: '#1d4ed8' },
  'sp-m':  { bg: '#EDE9FE', fg: '#6d28d9' },
  'sp-e':  { bg: '#FEF2F2', fg: '#b91c1c' },
  'sp-w':  { bg: '#FEF2F2', fg: '#b91c1c', border: '#FECACA' },
  'sp-o':  { bg: '#FFFBEB', fg: '#92400E', border: '#FCD34D' },
  'tp-px': { bg: '#DBEAFE', fg: '#1d4ed8' },
  'tp-ca': { bg: '#EDE9FE', fg: '#6d28d9' },
};

const PROXY_HEADERS = ['Category', 'Date', 'Client', 'Event Date', 'Job #', 'NAA', 'Job Status', 'Scan #', 'PK Slip', 'Location', 'Actions'];
const REG_HEADERS   = ['Status', 'Distributor', 'Date', 'PK Slip', 'Error', 'Client', 'Book Name', 'Material', 'Job #', 'Addressed', 'Scan #', 'Lit Code', 'Counted', 'Qty', 'Effective', 'Location', 'Box', 'Carrier', 'Tracking', 'Notes', 'FFC Only', 'Actions'];

const ICONS = {
  edit: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  file: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
};

function StatusPill({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted">—</span>;
  const s = STATUS_MAP[status] || { cls: 'sp-pending' };
  return <span className={`sp text-xs ${s.cls}`}>{status}</span>;
}

function CategoryTag({ category }: { category: string | null }) {
  const c = (category && CATEGORY[category as keyof typeof CATEGORY]) || CATEGORY.P;
  return <span className={c.cls}>{c.label}</span>;
}

function SortIcon({ active, direction }: { active: boolean; direction: string }) {
  return (
    <span className="inline-flex items-center ml-1">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={active ? 'text-teal-600' : 'text-[#C4C5CC]'}>
        <path d="M5 2L8 6H2L5 2Z" fill={active && direction === 'asc' ? 'currentColor' : '#D1D5DB'} />
        <path d="M5 8L2 4H8L5 8Z" fill={active && direction === 'desc' ? 'currentColor' : '#D1D5DB'} />
      </svg>
    </span>
  );
}

function ActionButton({ icon, label, onClick }: { icon: any; label: string; onClick: () => void }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="w-7 h-7 rounded flex items-center justify-center transition-colors text-copy hover:bg-wash hover:text-ink"
      title={label}
    >
      {icon}
    </button>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}

const docType = (r: MaterialReceiptRow) => r.received_docs ? Object.keys(r.received_docs).join(', ') : '';
const docQty  = (r: MaterialReceiptRow) => r.received_docs ? Object.values(r.received_docs).filter(v => v != null).join(', ') : '';
const ynBlank = (b: boolean | null) => (b == null ? '' : b ? 'Y' : 'N');

// 'X' means "printed" (see Production Log, which lists regulatory_stage ===
// 'completed' jobs) — job_status itself doesn't reliably carry that, so
// derive it from regulatory_stage instead of trusting a stored 'X' value.
const regJobStatus = (r: MaterialReceiptRow): string | null =>
  r.regulatory_stage === 'completed' ? 'X' : r.job_status === 'X' ? null : r.job_status;

// Display value for a given column — also used as the sort key. Status is
// derived (not a stored field), so its value comes from a precomputed
// scan_number -> RegulatoryStatus map rather than the receipt itself.
function cellValue(r: MaterialReceiptRow, header: string, regStatusMap: Record<string, string>): string {
  switch (header) {
    case 'Category':   return CATEGORY[r.job_category as keyof typeof CATEGORY]?.label ?? '';
    case 'Status':      return regStatusMap[r.scan_number] ?? '';
    case 'Distributor': return r.distributor ?? '';
    case 'Date':       return fmtDate(r.intake_date);
    case 'Client':     return r.client_names.join(', ');
    case 'Event Date': return fmtDate(r.event_date);
    case 'Job #':      return r.job_numbers.join(', ');
    case 'NAA':        return ynBlank(r.is_naa);
    case 'Job Status': return r.job_status ?? '';
    case 'Scan #':     return r.scan_number;
    case 'PK Slip':    return ynBlank(r.has_pk_slip);
    case 'Location':   return r.location ?? '';
    case 'Error':      return r.error_code ?? '';
    case 'Book Name':  return r.book_name ?? '';
    case 'Material':   return docType(r);
    case 'Addressed':  return r.addressed ?? '';
    case 'Lit Code':   return r.lit_code ?? '';
    case 'Counted':    return r.intake_by ?? '';
    case 'Qty':        return docQty(r);
    case 'Effective':  return fmtDate(r.effective_date);
    case 'Box':        return r.pk_boxes_count != null ? String(r.pk_boxes_count) : '';
    case 'Carrier':    return r.carrier ?? '';
    case 'Tracking':   return r.tracking ?? '';
    case 'Notes':      return (r.note_log || []).map(n => `${n.author} (${n.timestamp}): ${n.text}`).join(' | ');
    case 'FFC Only':   return r.ffc_only ?? '';
    default:           return '';
  }
}

export function ReceivingLogPage() {
  const { setTopbar } = useAppShell();
  const navigate = useNavigate();
  const { showToast } = (useOutletContext() as any) || {};
  const { user } = useAuth();
  const author = user?.name?.split(' ')[0] || 'User';

  const [activeTab, setActiveTab]       = useState('proxy');
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch]             = useState('');
  const [scope, setScope]               = useState<ScopeValue>({ window: 'today' });
  const searchRef = useRef<HTMLInputElement>(null);
  const [rows, setRows]                 = useState<MaterialReceiptRow[]>([]);
  const [loadError, setLoadError]       = useState<string | null>(null);
  const [editRow, setEditRow]           = useState<MaterialReceiptRow | null>(null);
  const [editDraft, setEditDraft]       = useState<ReceiptDraft | null>(null);
  const [savingEdit, setSavingEdit]     = useState(false);
  const [sort, setSort]                 = useState<{ column: string | null; direction: 'asc' | 'desc' }>({ column: null, direction: 'asc' });

  useEffect(() => { setTopbar('Receiving Log', 'Material receipts matched to jobs'); }, [setTopbar]);

  useEffect(() => {
    getMaterialReceipts().then(setRows).catch(e => setLoadError(e.message));
  }, []);

  // "/" focuses search; Esc clears it while focused.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'Escape' && document.activeElement === searchRef.current) {
        setSearch('');
        searchRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isReg = activeTab === 'regulatory';

  // Status column value for the Regulatory tab, keyed for sort lookups —
  // see regJobStatus() for how it's derived.
  const regStatusMap = useMemo(() => {
    const map: Record<string, string> = {};
    rows.forEach(r => {
      if (r.job_category === 'R') map[r.scan_number] = regJobStatus(r) ?? '';
    });
    return map;
  }, [rows]);

  // Time window is the primary axis for Proxy/Corp Action/Issuer — applied
  // before tab/status/search. Regulatory bypasses it: its Ready to Mail/
  // Completed rows track a multi-day production pipeline (a job received last
  // week can still be sitting in Ready to Mail), same as the Regulatory tab on
  // Material Intake, so Period is a monitoring lens only, not a row filter.
  const bounds = useMemo(() => windowBounds(scope), [scope]);
  const windowedRows = useMemo(() => rows.filter(r => r.job_category === 'R' || inWindow(r.intake_date, bounds)), [rows, bounds]);

  // Receiving Log only lists receipts with an actual matched job — exception
  // receipts (No Job Found / On Hold / Received Short) never get one and
  // belong in the Exceptions queue instead.
  const matchedRows = useMemo(() => windowedRows.filter(r => r.job_numbers.length > 0), [windowedRows]);

  // Badge counts for the TimeScope presets reflect the full dataset, not the window —
  // but still only matched receipts, so the badge never promises more than the table shows.
  const winCounts = useMemo(() => windowCounts(rows.filter(r => r.job_numbers.length > 0)), [rows]);

  const tabRows = useMemo(() => matchedRows.filter(r => (
    isReg ? r.job_category === 'R' : ['P', 'CA', 'I'].includes(r.job_category ?? '')
  )), [matchedRows, isReg]);

  const counts = useMemo(() => ({
    proxy:      matchedRows.filter(r => ['P', 'CA', 'I'].includes(r.job_category ?? '')).length,
    regulatory: matchedRows.filter(r => r.job_category === 'R').length,
  }), [matchedRows]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let data = tabRows.filter(r => {
      if (activeFilter !== 'All' && (isReg ? regJobStatus(r) : r.job_status) !== activeFilter) return false;
      if (!isReg && activeCategory !== 'All' && r.job_category !== activeCategory) return false;
      if (q) {
        const hay = `${r.client_names.join(' ')} ${r.job_numbers.join(' ')} ${r.scan_number}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    if (sort.column) {
      data = [...data].sort((a, b) => {
        const av = cellValue(a, sort.column!, regStatusMap).toLowerCase();
        const bv = cellValue(b, sort.column!, regStatusMap).toLowerCase();
        if (av < bv) return sort.direction === 'asc' ? -1 : 1;
        if (av > bv) return sort.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default: newest intake first (raw ISO sorts chronologically).
      data = [...data].sort((a, b) => (b.intake_date ?? '').localeCompare(a.intake_date ?? ''));
    }
    return data;
  }, [tabRows, activeFilter, activeCategory, search, sort, isReg, regStatusMap]);

  const truncated = scope.window === 'custom' && filtered.length > RENDER_CAP;
  const visible = truncated ? filtered.slice(0, RENDER_CAP) : filtered;

  const handleSort = useCallback((column: string) => {
    setSort(prev => prev.column === column
      ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { column, direction: 'asc' });
  }, []);

  const openAce = useCallback((r: MaterialReceiptRow) => {
    navigate('/ace', { state: { receiptThread: { receiptId: r.id, scan: r.scan_number, client: r.client_names.join(', '), row: r } } });
  }, [navigate]);

  const viewFile = useCallback((r: MaterialReceiptRow) => {
    navigate('/all-receipts', { state: { focusReceiptId: r.id, scan: r.scan_number } });
  }, [navigate]);

  const onSaved = useCallback((updated: MaterialReceiptRow) => {
    setRows(prev => prev.map(r => r.id === updated.id ? updated : r));
  }, []);

  // Edit Record opens the full ReceiptDrawer (all fields, incl. per-job Job Status).
  const openEdit = useCallback((r: MaterialReceiptRow) => {
    setEditRow(r);
    setEditDraft(draftFromRow(r));
  }, []);

  const closeEdit = useCallback(() => { setEditRow(null); setEditDraft(null); }, []);

  // Persist receipt columns + job match/status, then run the Overs workflow
  // (job Complete + received > ordered → flag a Pending Overs follow-up).
  const handleEditSave = useCallback(async () => {
    if (!editRow || !editDraft) return;
    setSavingEdit(true);
    try {
      let updated = await updateMaterialReceipt(editRow.id, buildReceiptPatch(editDraft));
      const jobs = draftToMatchJobs(editDraft);
      if (jobs.length) updated = await confirmReceiptMatch(editRow.id, jobs);
      const overs = await maybeCreateOvers(editRow.id, editDraft);
      onSaved(overs ?? updated);
      showToast?.(overs ? 'Saved · flagged Overs for review' : 'Record updated');
      closeEdit();
    } catch {
      showToast?.('Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  }, [editRow, editDraft, onSaved, showToast, closeEdit]);

  const setTab = useCallback((tabId: string) => {
    setActiveTab(tabId);
    setActiveFilter('All');
    setActiveCategory('All');
    setSort({ column: null, direction: 'asc' });
  }, []);

  const headers = isReg ? REG_HEADERS : PROXY_HEADERS;

  if (loadError) return <div className="px-6 py-5 text-sm text-red-600">{loadError}</div>;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tabs */}
      <div className="flex gap-0 px-6 border-b border-trim flex-shrink-0">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
            style={{ borderColor: activeTab === t.id ? '#2D7575' : 'transparent', color: activeTab === t.id ? '#256060' : '#73747A' }}>
            {t.label}
            <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{ background: activeTab === t.id ? '#2D7575' : '#F5F7FA', color: activeTab === t.id ? 'white' : '#73747A' }}>
              {t.id === 'proxy' ? counts.proxy : counts.regulatory}
            </span>
          </button>
        ))}
      </div>

      {/* Global header — time scope on a recessed band */}
      <PageHeader
        scope={scope}
        onScopeChange={setScope}
        counts={winCounts}
      />

      {/* Filters */}
      <div className="flex items-center gap-3 px-6 py-3 flex-shrink-0">
        {!isReg && (<>
          <div className="flex gap-1.5 flex-wrap">
            {CATEGORY_FILTERS.map(c => (
              <button key={c.id} onClick={() => setActiveCategory(c.id)}
                className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
                style={pillStyle(activeCategory === c.id, PILL_PALETTE[CATEGORY[c.id as keyof typeof CATEGORY]?.cls.split(' ')[1]])}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="w-px h-5 bg-trim" />
        </>)}
        {/* Job status — a compact dropdown rather than a pill row; it's used rarely. */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted">Job Status</span>
          <select value={activeFilter} onChange={e => setActiveFilter(e.target.value)}
            className="px-2.5 py-1 rounded-lg border border-trim text-xs text-ink bg-white outline-none focus:border-teal-400 cursor-pointer">
            {FILTERS[activeTab].map(f => (
              <option key={f} value={f}>{f === 'All' ? 'All statuses' : f}</option>
            ))}
          </select>
        </div>
        <div className="ml-auto relative">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" width="12" height="12" viewBox="0 0 13 13" fill="none">
            <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M9.5 9.5l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients, jobs, scans…"
            className="pl-7 pr-7 py-1 rounded-lg border border-trim text-xs outline-none focus:border-teal-400 bg-white" style={{ width: 240 }} />
          {!search && (
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-medium text-muted bg-wash border border-trim rounded px-1 py-0.5 leading-none pointer-events-none">/</kbd>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        <div className={isReg ? 'overflow-x-auto' : ''}>
          <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: isReg ? 1800 : undefined }}>
            <thead>
              <tr>
                {headers.map(h => (
                  <th key={h} onClick={() => h !== 'Actions' && handleSort(h)}
                    className={`text-left text-xs font-semibold uppercase tracking-wide px-2 py-2 border-b border-trim whitespace-nowrap bg-wash sticky top-0 ${h !== 'Actions' ? 'cursor-pointer hover:bg-[#E9F0F0] select-none' : ''}`}
                    style={{ color: '#73747A' }}>
                    <span className="flex items-center">
                      {h}
                      {h !== 'Actions' && <SortIcon active={sort.column === h} direction={sort.direction} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map(r => (
                <tr key={r.id} className="group hover:bg-wash transition-colors cursor-pointer" onClick={() => openAce(r)}>
                  {isReg ? (<>
                    <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap">
                      <StatusPill status={regJobStatus(r)} />
                    </td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.distributor}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap">{fmtDate(r.intake_date)}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy"><span className={r.has_pk_slip ? 'text-teal-600 font-semibold' : 'text-muted'}>{ynBlank(r.has_pk_slip)}</span></td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.error_code}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs font-medium text-ink whitespace-nowrap">{r.client_names.join(', ')}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy max-w-[180px] truncate" title={r.book_name ?? ''}>{r.book_name}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{docType(r)}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.job_numbers.join(', ')}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.addressed}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.scan_number}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.lit_code}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.intake_by}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap tabular-nums">{docQty(r)}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap">{fmtDate(r.effective_date)}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.location}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.pk_boxes_count}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.carrier}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.tracking}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy max-w-[150px] truncate" title={(r.note_log || []).map(n => `${n.author} (${n.timestamp}): ${n.text}`).join('\n')}>{(r.note_log || []).slice(-1)[0]?.text ?? ''}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.ffc_only}</td>
                  </>) : (<>
                    <td className="px-2 py-1.5 border-b border-wash"><CategoryTag category={r.job_category} /></td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap">{fmtDate(r.intake_date)}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs font-medium text-ink max-w-[200px] truncate" title={r.client_names.join(', ')}>{r.client_names.join(', ')}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap">{fmtDate(r.event_date)}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.job_numbers.join(', ')}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{ynBlank(r.is_naa)}</td>
                    <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap"><StatusPill status={r.job_status} /></td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.scan_number}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{ynBlank(r.has_pk_slip)}</td>
                    <td className="px-2 py-1.5 border-b border-wash text-xs text-copy whitespace-nowrap">{r.location}</td>
                  </>)}
                  <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-0.5">
                      <ActionButton icon={ICONS.edit} label="Edit" onClick={() => openEdit(r)} />
                      <ActionButton icon={ICONS.file} label="View file" onClick={() => viewFile(r)} />
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={headers.length} className="text-center py-12 text-muted text-sm">No records match the current filter.</td></tr>
              )}
              {truncated && (
                <tr><td colSpan={headers.length} className="text-center py-4 text-muted text-xs bg-wash">
                  Showing first {RENDER_CAP} of {filtered.length.toLocaleString()} — narrow the date range to see more.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ReceiptDrawer
        open={!!editDraft}
        draft={editDraft}
        author={author}
        onChange={patch => setEditDraft(d => (d ? { ...d, ...patch } : d))}
        onClose={() => { if (!savingEdit) closeEdit(); }}
        onSave={handleEditSave}
      />
    </div>
  );
}
